import { useEffect, useState } from 'react';
import {
    Button,
    ButtonSize,
    ButtonVariant,
    H2Emphasized,
    MultilineInput,
    Spinner,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { GroveSimpleModal } from '../../common/components/GroveSimpleModal';
import { TextError } from '../../common/components/InlineError';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import {
    useGetPrompt,
    useUpdatePrompt,
} from '../../llm-service/hooks/usePrompts';
import { captureException } from '../../logger/Logger';

interface PromptEditorModalProps {
    isOpen: boolean;
    promptKey: string;
    onClose: () => void;
}

const PromptEditorModal = ({
    isOpen,
    promptKey,
    onClose,
}: PromptEditorModalProps) => {
    const [editedContent, setEditedContent] = useState('');
    const [hasChanges, setHasChanges] = useState(false);

    const toast = useToastController();
    const { data: promptData, isLoading, error } = useGetPrompt(promptKey);
    const { mutateAsync: updatePrompt, isPending } = useUpdatePrompt();

    useEffect(() => {
        if (promptData?.content != null) {
            setEditedContent(promptData.content);
        }
    }, [promptData]);

    useEffect(() => {
        if (promptData?.content != null) {
            setHasChanges(editedContent !== promptData.content);
        }
    }, [editedContent, promptData]);

    const handleSave = async () => {
        try {
            await updatePrompt({
                promptKey,
                content: editedContent,
            });
            toast.show(
                'Prompt updated successfully',
                MutationSuccessToastOptions,
            );
            onClose();
        } catch (error) {
            toast.show(
                'Failed to update prompt',
                MutationErrorToastOptions(error),
            );
            captureException(error);
        }
    };

    const handleCancel = () => {
        if (promptData?.content != null) {
            setEditedContent(promptData.content);
            setHasChanges(false);
        }
        onClose();
    };

    return (
        <GroveSimpleModal isOpen={isOpen} onClose={handleCancel} hasCloseButton>
            <YStack gap={16} padding={24} width={1200}>
                {isLoading ? (
                    <YStack padding={24} alignItems="center">
                        <Spinner />
                    </YStack>
                ) : error ? (
                    <YStack padding={24}>
                        <TextError message={error.message} />
                    </YStack>
                ) : (
                    <>
                        <H2Emphasized>{promptData?.displayName}</H2Emphasized>

                        <MultilineInput
                            value={editedContent}
                            onChangeText={setEditedContent}
                            numberOfLines={25}
                            placeholder="Prompt..."
                            width="100%"
                        />

                        <XStack gap={12} justifyContent="flex-end">
                            <Button
                                text="Cancel"
                                onPress={handleCancel}
                                variant={ButtonVariant.SECONDARY}
                                size={ButtonSize.SMALL}
                                width="auto"
                            />
                            <Button
                                text="Save"
                                onPress={handleSave}
                                variant={ButtonVariant.PRIMARY}
                                size={ButtonSize.SMALL}
                                width="auto"
                                disabled={!hasChanges || isPending}
                            />
                        </XStack>
                    </>
                )}
            </YStack>
        </GroveSimpleModal>
    );
};

export default PromptEditorModal;
